'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Loader2, ChevronLeft, ChevronRight, ChevronDown, HelpCircle } from 'lucide-react';
import { fmtMoney } from './AdsAnalytics';

// raw stored row
interface Row {
  date: string;
  ad_spend: number;
  impressions: number;
  clicks: number;
  opt_ins: number;
  calls_booked: number;
  calls_had: number;
  sales: number;
  projected_ltv: number;
  cash_collected: number;
  notes: string;
}

const BLANK = (date: string): Row => ({
  date, ad_spend: 0, impressions: 0, clicks: 0, opt_ins: 0,
  calls_booked: 0, calls_had: 0, sales: 0, projected_ltv: 0, cash_collected: 0, notes: '',
});

// editable manual columns (everything else is auto from Meta or calculated)
const EDIT_FIELDS = ['calls_booked', 'calls_had', 'sales', 'projected_ltv', 'cash_collected'] as const;
type EditField = typeof EDIT_FIELDS[number];

function derive(r: Row) {
  const { ad_spend: s, impressions: im, clicks: c, opt_ins: o, calls_booked: cb, calls_had: ch, sales: sa, projected_ltv: ltv, cash_collected: cash } = r;
  return {
    ctr: im > 0 ? (c / im) * 100 : 0,
    cpc: c > 0 ? s / c : 0,
    optCvr: c > 0 ? (o / c) * 100 : 0,
    cpl: o > 0 ? s / o : 0,
    vslCvr: o > 0 ? (cb / o) * 100 : 0,
    costPerCall: cb > 0 ? s / cb : 0,
    showUp: cb > 0 ? (ch / cb) * 100 : 0,
    costPerShow: ch > 0 ? s / ch : 0,
    closeRate: ch > 0 ? (sa / ch) * 100 : 0,
    cpa: sa > 0 ? s / sa : 0,
    pRoas: s > 0 ? ltv / s : 0,
    cRoas: s > 0 ? cash / s : 0,
    upfrontPL: cash - s,
    projPL: ltv - s,
  };
}

function daysOf(month: string): string[] {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}
function thisMonth(): string { return new Date().toISOString().slice(0, 7); }
function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}
function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const PL = (n: number, cur: string) => (
  <span className={n > 0 ? 'text-green-400' : n < 0 ? 'text-red-400' : 'text-gray-500'}>{fmtMoney(n, cur)}</span>
);

// column metadata — drives header tooltips, source colours, and the guide panel
type Src = 'meta' | 'manual' | 'calc' | 'both';
interface Col { label: string; group: string; src: Src; tip: string }
const COLUMNS: Col[] = [
  { label: 'Date', group: '', src: 'calc', tip: 'Day of the month' },
  { label: 'Spend', group: 'Ad data', src: 'meta', tip: 'Ad spend that day — auto-pulled from Meta (Sync ad data)' },
  { label: 'Clicks', group: 'Ad data', src: 'meta', tip: 'Link clicks — auto from Meta' },
  { label: 'CTR', group: 'Ad data', src: 'calc', tip: 'Click-through rate = clicks ÷ impressions' },
  { label: 'CPC', group: 'Ad data', src: 'calc', tip: 'Cost per click = spend ÷ clicks' },
  { label: 'Opt-Ins', group: 'Opt-in', src: 'meta', tip: 'Leads who opted in — auto-pulled from your Meta “Opt-In” custom conversion' },
  { label: 'Opt CVR', group: 'Opt-in', src: 'calc', tip: 'Opt-in rate = opt-ins ÷ clicks (landing-page conversion)' },
  { label: 'CPL', group: 'Opt-in', src: 'calc', tip: 'Cost per lead = spend ÷ opt-ins' },
  { label: 'Calls Bkd', group: 'Calls', src: 'both', tip: 'Calls booked — auto-fills from your Meta “Call Booking” conversion, but editable: type your funnel’s real count to override (a Meta sync won’t overwrite it).' },
  { label: 'VSL CVR', group: 'Calls', src: 'calc', tip: 'Booking rate = calls booked ÷ opt-ins (how many leads book a call)' },
  { label: 'Cost/Call', group: 'Calls', src: 'calc', tip: 'Cost per booked call = spend ÷ calls booked' },
  { label: 'Calls Had', group: 'Calls', src: 'manual', tip: 'YOU FILL IN: calls that actually happened (showed up) — from your CRM' },
  { label: 'Show Up', group: 'Calls', src: 'calc', tip: 'Show-up rate = calls had ÷ calls booked' },
  { label: 'Cost/Show', group: 'Calls', src: 'calc', tip: 'Cost per call held = spend ÷ calls had' },
  { label: 'Sales', group: 'Sales', src: 'manual', tip: 'YOU FILL IN: closed deals that day — from your CRM / payment processor' },
  { label: 'Close', group: 'Sales', src: 'calc', tip: 'Close rate = sales ÷ calls had' },
  { label: 'CPA', group: 'Sales', src: 'calc', tip: 'Cost per acquisition = spend ÷ sales' },
  { label: 'Proj LTV', group: 'Economics', src: 'manual', tip: 'YOU FILL IN: projected lifetime value of that day’s sales (per-customer LTV × sales, or a total)' },
  { label: 'Cash', group: 'Economics', src: 'manual', tip: 'YOU FILL IN: cash actually collected that day — from your payment processor' },
  { label: 'P-ROAS', group: 'Economics', src: 'calc', tip: 'Projected ROAS = projected LTV ÷ spend' },
  { label: 'C-ROAS', group: 'Economics', src: 'calc', tip: 'Cash ROAS = cash collected ÷ spend' },
  { label: 'Upfront P&L', group: 'Economics', src: 'calc', tip: 'Cash collected − spend (immediate profit/loss)' },
  { label: 'Proj P&L', group: 'Economics', src: 'calc', tip: 'Projected LTV − spend (projected profit/loss)' },
];
const SRC_COLOR: Record<Src, string> = { meta: 'text-sky-400', manual: 'text-cyan-300', calc: 'text-gray-500', both: 'text-teal-300' };
const GROUP_STARTS = new Set(['Spend', 'Opt-Ins', 'Calls Bkd', 'Sales', 'Proj LTV']);

function TrackerGuide() {
  const [open, setOpen] = useState(false);
  const manual = COLUMNS.filter(c => c.src === 'manual');
  return (
    <div className="glass-card">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5 text-left">
        <span className="text-sm font-medium text-white flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-cyan-400" /> How to use this tracker
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 text-xs text-gray-400 border-t border-white/[0.04] pt-3">
          <p>
            The funnel flows <span className="text-gray-200">Spend → Clicks → Opt-Ins → Calls Booked → Calls Had → Sales → Cash</span>.
            <span className="text-sky-400"> Blue</span> columns auto-fill from Meta when you hit <span className="text-gray-200">Sync ad data</span>;
            <span className="text-cyan-300"> cyan</span> columns you fill in from your CRM; grey columns are calculated automatically.
          </p>
          <div>
            <p className="text-gray-300 font-medium mb-1.5">You fill in each day (from your CRM / calendar / payment processor):</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {manual.map(c => (
                <div key={c.label} className="flex gap-2">
                  <span className="text-cyan-300 font-medium whitespace-nowrap">{c.label}</span>
                  <span className="text-gray-500">— {c.tip.replace('YOU FILL IN: ', '')}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-gray-400">
            <span className="text-teal-300 font-medium">Calls Booked</span> auto-fills from Meta’s “Call Booking” conversion — but you can type over it with your funnel’s real number, and a Meta sync won’t overwrite your edit.
          </p>
          <p className="text-gray-500">
            <span className="text-gray-300">To measure effectiveness:</span> watch <span className="text-gray-300">CPL</span> (lead cost),
            <span className="text-gray-300"> Cost/Call</span> &amp; <span className="text-gray-300">Show Up%</span> (call quality),
            <span className="text-gray-300"> CPA</span> &amp; <span className="text-gray-300">Close%</span> (sales efficiency), and
            <span className="text-gray-300"> C-ROAS / Upfront P&amp;L</span> (is the campaign actually profitable). Hover any column header for its definition.
          </p>
        </div>
      )}
    </div>
  );
}

export function TrackerGrid({ accountId, currency, liveRefresh, campaign = '__ALL__' }: { accountId: string; currency: string; liveRefresh: boolean; campaign?: string }) {
  const cq = campaign && campaign !== '__ALL__' ? `&campaign=${campaign}` : '';
  const cqSolo = campaign && campaign !== '__ALL__' ? `?campaign=${campaign}` : '';
  const [month, setMonth] = useState<string>(thisMonth());
  const [rowMap, setRowMap] = useState<Record<string, Row>>({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const cur = currency || '';
  const autoSynced = useRef<Set<string>>(new Set());

  const applyRows = (rows: Row[]) => {
    const map: Record<string, Row> = {};
    for (const r of rows) map[r.date] = r;
    setRowMap(map);
  };

  const doSync = useCallback((acct: string, mo: string, silent: boolean) => {
    setSyncing(true);
    fetch(`/api/ads/tracker/${acct}/sync?month=${mo}${cq}`, { method: 'POST' })
      .then(async r => ({ ok: r.ok, body: await r.json() }))
      .then(({ ok, body }) => {
        if (ok) {
          applyRows(body.rows as Row[]);
          if (!silent) toast.success(body.synced ? `Synced ${body.synced} days from Meta` : 'No Meta data for this month');
        } else if (!silent) toast.error(body.message || body.error || 'Sync failed', { duration: 7000 });
      })
      .catch(e => { if (!silent) toast.error(e.message); })
      .finally(() => setSyncing(false));
  }, [cq]);

  const load = useCallback((acct: string, mo: string) => {
    setLoading(true);
    fetch(`/api/ads/tracker/${acct}?month=${mo}${cq}`)
      .then(r => r.json())
      .then(d => {
        applyRows((d.rows || []) as Row[]);
        setLoading(false);
        // auto-pull ad data from Meta the first time a month with no spend is opened
        const hasAd = (d.rows || []).some((r: Row) => r.ad_spend > 0 || r.clicks > 0);
        const key = `${acct}:${mo}:${campaign}`;
        if (!hasAd && d.liveRefresh && !autoSynced.current.has(key)) {
          autoSynced.current.add(key);
          doSync(acct, mo, true);
        }
      })
      .catch(() => setLoading(false));
  }, [doSync, cq, campaign]);

  useEffect(() => {
    if (!accountId) return;
    load(accountId, month);
  }, [accountId, month, load, campaign]);

  const save = (date: string, field: EditField, value: number) => {
    setRowMap(prev => ({ ...prev, [date]: { ...(prev[date] || BLANK(date)), [field]: value } }));
    fetch(`/api/ads/tracker/${accountId}${cqSolo}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, patch: { [field]: value } }),
    }).catch(() => toast.error('Save failed'));
  };

  const sync = () => doSync(accountId, month, false);

  const days = daysOf(month);
  const rows = days.map(d => rowMap[d] || BLANK(d));
  // totals from summed raw inputs, then derive
  const sum = rows.reduce((a, r) => {
    (['ad_spend', 'impressions', 'clicks', 'opt_ins', 'calls_booked', 'calls_had', 'sales', 'projected_ltv', 'cash_collected'] as const)
      .forEach(k => { a[k] = (a[k] || 0) + r[k]; });
    return a;
  }, {} as any);
  const totalRow: Row = { date: 'Total', notes: '', ...sum };
  const td = derive(totalRow);

  const num = (v: number, d = 0) => (v ? v.toLocaleString(undefined, { maximumFractionDigits: d }) : '—');
  const pctCell = (v: number) => (v ? `${v.toFixed(1)}%` : '—');

  return (
    <div className="space-y-3">
      <TrackerGuide />
      {/* toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <button onClick={() => setMonth(shiftMonth(month, -1))} className="p-2 rounded-lg border border-white/[0.08] bg-white/[0.05] text-gray-400 hover:text-white">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value || thisMonth())}
            className="bg-[#0d1117]/85 border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 [color-scheme:dark]"
          />
          <button onClick={() => setMonth(shiftMonth(month, 1))} className="p-2 rounded-lg border border-white/[0.08] bg-white/[0.05] text-gray-400 hover:text-white">
            <ChevronRight className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-400 ml-2">{monthLabel(month)}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-gray-600 hidden md:inline">
            <span className="text-sky-400">●</span> Meta · <span className="text-teal-300">●</span> Meta+edit · <span className="text-cyan-300">●</span> you fill · <span className="text-gray-500">●</span> calc
          </span>
          <button
            onClick={sync}
            disabled={syncing}
            title={liveRefresh ? 'Pull spend / clicks / opt-ins from Meta for this month' : 'Needs META_ACCESS_TOKEN'}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.05] text-sm text-gray-300 hover:text-white hover:border-cyan-500/40 disabled:opacity-50"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Sync ad data
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="h-7 w-7 animate-spin text-cyan-400" /></div>
      ) : (
        <div className="glass-card overflow-x-auto">
          <table className="text-xs whitespace-nowrap">
            <thead>
              <tr className="text-[9px] uppercase border-b border-white/[0.04]">
                {COLUMNS.map((c, i) => (
                  <th
                    key={c.label}
                    title={c.tip}
                    className={`font-medium py-2 cursor-help ${SRC_COLOR[c.src]} ${
                      i === 0 ? 'text-left px-3 sticky left-0 bg-[#0d1117]/85 z-10' : 'text-right px-2'
                    } ${i === COLUMNS.length - 1 ? 'pr-3' : ''} ${GROUP_STARTS.has(c.label) ? 'border-l border-white/[0.04]' : ''}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const d = derive(r);
                const dayNum = Number(r.date.slice(-2));
                return (
                  <tr key={r.date} className="border-t border-white/[0.04] hover:bg-white/[0.015]">
                    <td className="px-3 py-1 text-gray-400 sticky left-0 bg-[#0d1117]/85/95 z-10">{r.date.slice(5)} <span className="text-gray-700">·{dayNum}</span></td>
                    <td className="px-2 py-1 text-right text-gray-300 border-l border-white/[0.04]">{r.ad_spend ? fmtMoney(r.ad_spend, cur) : '—'}</td>
                    <td className="px-2 py-1 text-right text-gray-400">{num(r.clicks)}</td>
                    <td className="px-2 py-1 text-right text-gray-500">{pctCell(d.ctr)}</td>
                    <td className="px-2 py-1 text-right text-gray-400">{d.cpc ? fmtMoney(d.cpc, cur) : '—'}</td>
                    <td className="px-2 py-1 text-right text-gray-300 border-l border-white/[0.04]">{r.opt_ins ? num(r.opt_ins) : '—'}</td>
                    <td className="px-2 py-1 text-right text-gray-500">{pctCell(d.optCvr)}</td>
                    <td className="px-2 py-1 text-right text-gray-400">{d.cpl ? fmtMoney(d.cpl, cur) : '—'}</td>
                    <EditCell value={r.calls_booked} onCommit={v => save(r.date, 'calls_booked', v)} className="border-l border-white/[0.04]" />
                    <td className="px-2 py-1 text-right text-gray-500">{pctCell(d.vslCvr)}</td>
                    <td className="px-2 py-1 text-right text-gray-400">{d.costPerCall ? fmtMoney(d.costPerCall, cur) : '—'}</td>
                    <EditCell value={r.calls_had} onCommit={v => save(r.date, 'calls_had', v)} />
                    <td className="px-2 py-1 text-right text-gray-500">{pctCell(d.showUp)}</td>
                    <td className="px-2 py-1 text-right text-gray-400">{d.costPerShow ? fmtMoney(d.costPerShow, cur) : '—'}</td>
                    <EditCell value={r.sales} onCommit={v => save(r.date, 'sales', v)} className="border-l border-white/[0.04]" />
                    <td className="px-2 py-1 text-right text-gray-500">{pctCell(d.closeRate)}</td>
                    <td className="px-2 py-1 text-right text-gray-400">{d.cpa ? fmtMoney(d.cpa, cur) : '—'}</td>
                    <EditCell value={r.projected_ltv} onCommit={v => save(r.date, 'projected_ltv', v)} money className="border-l border-white/[0.04]" />
                    <EditCell value={r.cash_collected} onCommit={v => save(r.date, 'cash_collected', v)} money />
                    <td className="px-2 py-1 text-right text-gray-400">{d.pRoas ? d.pRoas.toFixed(2) : '—'}</td>
                    <td className="px-2 py-1 text-right text-gray-400">{d.cRoas ? d.cRoas.toFixed(2) : '—'}</td>
                    <td className="px-2 py-1 text-right">{PL(d.upfrontPL, cur)}</td>
                    <td className="px-3 py-1 text-right">{PL(d.projPL, cur)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-700 bg-white/[0.02] font-semibold text-gray-200">
                <td className="px-3 py-2 sticky left-0 bg-[#0d1117]/85 z-10">Total</td>
                <td className="px-2 py-2 text-right border-l border-white/[0.04]">{fmtMoney(sum.ad_spend || 0, cur)}</td>
                <td className="px-2 py-2 text-right">{num(sum.clicks)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{pctCell(td.ctr)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{td.cpc ? fmtMoney(td.cpc, cur) : '—'}</td>
                <td className="px-2 py-2 text-right border-l border-white/[0.04]">{num(sum.opt_ins)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{pctCell(td.optCvr)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{td.cpl ? fmtMoney(td.cpl, cur) : '—'}</td>
                <td className="px-2 py-2 text-right text-teal-300 border-l border-white/[0.04]">{num(sum.calls_booked)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{pctCell(td.vslCvr)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{td.costPerCall ? fmtMoney(td.costPerCall, cur) : '—'}</td>
                <td className="px-2 py-2 text-right text-cyan-400">{num(sum.calls_had)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{pctCell(td.showUp)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{td.costPerShow ? fmtMoney(td.costPerShow, cur) : '—'}</td>
                <td className="px-2 py-2 text-right text-cyan-400 border-l border-white/[0.04]">{num(sum.sales)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{pctCell(td.closeRate)}</td>
                <td className="px-2 py-2 text-right text-gray-400">{td.cpa ? fmtMoney(td.cpa, cur) : '—'}</td>
                <td className="px-2 py-2 text-right text-cyan-400 border-l border-white/[0.04]">{sum.projected_ltv ? fmtMoney(sum.projected_ltv, cur) : '—'}</td>
                <td className="px-2 py-2 text-right text-cyan-400">{sum.cash_collected ? fmtMoney(sum.cash_collected, cur) : '—'}</td>
                <td className="px-2 py-2 text-right text-gray-400">{td.pRoas ? td.pRoas.toFixed(2) : '—'}</td>
                <td className="px-2 py-2 text-right text-gray-400">{td.cRoas ? td.cRoas.toFixed(2) : '—'}</td>
                <td className="px-2 py-2 text-right">{PL(td.upfrontPL, cur)}</td>
                <td className="px-3 py-2 text-right">{PL(td.projPL, cur)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <p className="text-[10px] text-gray-600">
        Cyan columns are editable (saved instantly). Spend / clicks come from Meta — click <span className="text-gray-400">Sync ad data</span> to pull this month. Derived columns (CVR, cost-per, ROAS, P&amp;L) recalculate live. Values in {cur || 'account currency'}.
      </p>
    </div>
  );
}

function EditCell({ value, onCommit, money, className }: { value: number; onCommit: (v: number) => void; money?: boolean; className?: string }) {
  const [v, setV] = useState<string>(value ? String(value) : '');
  const focused = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // sync from server value only while NOT editing, so a background refresh
  // never wipes what's being typed
  useEffect(() => { if (!focused.current) setV(value ? String(value) : ''); }, [value]);

  const commit = (raw: string) => { const n = parseFloat(raw) || 0; if (n !== value) onCommit(n); };

  return (
    <td className={`px-1 py-1 text-right bg-cyan-500/[0.04] ${className || ''}`}>
      <input
        value={v}
        inputMode="decimal"
        onFocus={() => { focused.current = true; }}
        onChange={e => {
          const clean = e.target.value.replace(/[^0-9.\-]/g, '');
          setV(clean);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => commit(clean), 500); // save as you type
        }}
        onBlur={() => { focused.current = false; if (timer.current) clearTimeout(timer.current); commit(v); }}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        placeholder={money ? '0' : '—'}
        className="w-14 bg-transparent text-right text-gray-100 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 rounded px-1 py-0.5 placeholder:text-gray-700"
      />
    </td>
  );
}
